import {
  Button,
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Switch,
  useToast,
} from '@ais-ui/ui-core';
import * as z from 'zod';
import { useSettings } from '../SettingsProvider';
import { useSaveSettings } from '@ais-ui/websockets';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { cn } from '@ais-ui/utils';
import React, { useEffect, useState } from 'react';
import { useLocalization } from '@ais-ui/locale-management';
import { useSettingsPage } from '../SettingsPageProvider';

const trackFormSchema = z.object({
  enable_track: z.boolean(),
  show_samples: z.boolean(),
  show_direction_arrows: z.boolean(),
  interval_in_minutes: z.number(),
  history_in_hours: z.number(),
  track_max_position_jump_in_meters: z.number(),
  track_max_position_jump_in_knots: z.number(),
});

type TrackFormValues = z.infer<typeof trackFormSchema>;

export const TrackForm = () => {
  const { messages } = useLocalization();
  const { toast } = useToast();
  const { settings } = useSettings();

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const { mutate: mutateSettings } = useSaveSettings();

  const initialValues = {
    enable_track: settings['ribbonPlugin.settings'].enable_history,
    history_in_hours: settings['ribbonPlugin.settings'].track_lookback_time,
    interval_in_minutes:
      settings['ribbonPlugin.settings'].track_timestamp_interval,
    show_samples: settings['ribbonPlugin.settings'].track_show_samples,
    show_direction_arrows: settings['ribbonPlugin.settings'].track_show_dir,
    track_max_position_jump_in_meters:
      settings['ribbonPlugin.settings'].track_max_pos_jump,
    track_max_position_jump_in_knots:
      settings['ribbonPlugin.settings'].track_max_speed,
  };

  const form = useForm<TrackFormValues>({
    resolver: zodResolver(trackFormSchema),
    defaultValues: initialValues,
  });

  const { setUnsavedSettings } = useSettingsPage();

  useEffect(() => {
    const subscription = form.watch((formValues) => {
      const hasChanges =
        JSON.stringify(formValues) !== JSON.stringify(initialValues);
      setUnsavedSettings(hasChanges);
    });

    return () => subscription.unsubscribe();
  }, [form, form.watch, initialValues, setUnsavedSettings]);

  const enableTrack = form.getValues().enable_track;

  function onSubmit(data: TrackFormValues) {
    setIsSubmitting(true);
    const settingsToSave = {
      ...settings,
      'ribbonPlugin.settings': {
        ...settings['ribbonPlugin.settings'],
        enable_history: data.enable_track,
        track_show_samples: data.show_samples,
        track_timestamp_interval: data.interval_in_minutes,
        track_lookback_time: data.history_in_hours,
        track_show_dir: data.show_direction_arrows,
        track_max_pos_jump: data.track_max_position_jump_in_meters,
        track_max_speed: data.track_max_position_jump_in_knots,
      },
    };
    mutateSettings(settingsToSave, {
      onSuccess: () => {
        toast({
          className: 'update-track-enable-success-toast',
          title: messages.genericToastTitleSuccess,
          description: messages.settingsMapTrackSuccessUpdateMessage,
        });
        setIsSubmitting(false);
        setUnsavedSettings(false);
      },
      onError: () => {
        toast({
          className: 'update-track-settings-error-toast',
          title: messages.genericToastTitleError,
          description: messages.settingsMapTrackFailedUpdateMessage,
          variant: 'destructive',
        });
        setIsSubmitting(false);
      },
    });
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
        <div>
          <div className="space-y-4">
            <h3 className="text-lg font-medium">
              {messages.settingsMapTrackTitle}
            </h3>
            <div className="space-y-4">
              <FormField
                key="enable_track"
                control={form.control}
                name="enable_track"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between border rounded-lg p-4">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">
                        {messages.settingsMapTrackEnableTrackLabel}
                      </FormLabel>
                      <FormDescription>
                        {messages.settingsMapTrackEnableTrackDescription}
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={(value) => {
                          field.onChange(value);
                        }}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                key="history_in_hours"
                control={form.control}
                name="history_in_hours"
                render={({ field }) => (
                  <FormItem
                    className={cn(
                      'flex flex-row items-center justify-between border rounded-lg p-4',
                      !enableTrack && 'opacity-50'
                    )}
                  >
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">
                        {messages.settingsMapTrackHistoryLabel}
                      </FormLabel>
                      <FormDescription>
                        {messages.settingsMapTrackHistoryDescription}{' '}
                      </FormDescription>
                    </div>
                    <FormControl
                      id="settings-track-duration"
                      onChange={() => form.clearErrors()}
                    >
                      <Input
                        className="w-20"
                        disabled={!enableTrack}
                        type="number"
                        {...field}
                        onChange={(e) => {
                          if (!e.target.value) {
                            field.onChange('');
                          } else {
                            // Convert the string value to a number
                            const numberValue = parseFloat(e.target.value);
                            field.onChange(numberValue);
                          }
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                key="show_samples"
                control={form.control}
                name="show_samples"
                render={({ field }) => (
                  <FormItem
                    className={cn(
                      'flex flex-row items-center justify-between border rounded-lg p-4',
                      !enableTrack && 'opacity-50'
                    )}
                  >
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">
                        {messages.settingsMapTrackSamplesLabel}
                      </FormLabel>
                      <FormDescription>
                        {messages.settingsMapTrackSamplesDescription}
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        id="samples-button"
                        disabled={!enableTrack}
                        checked={field.value}
                        onCheckedChange={(value) => {
                          field.onChange(value);
                        }}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                key="show_direction_arrows"
                control={form.control}
                name="show_direction_arrows"
                render={({ field }) => (
                  <FormItem
                    className={cn(
                      'flex flex-row items-center justify-between border rounded-lg p-4',
                      !enableTrack && 'opacity-50'
                    )}
                  >
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">
                        {messages.settingsMapTrackDirectionalArrowsLabel}
                      </FormLabel>
                      <FormDescription>
                        {messages.settingsMapTrackDirectionalArrowsDescription}
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        disabled={!enableTrack}
                        checked={field.value}
                        onCheckedChange={(value) => {
                          field.onChange(value);
                        }}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                key="interval_in_minutes"
                control={form.control}
                name="interval_in_minutes"
                render={({ field }) => (
                  <FormItem
                    className={cn(
                      'flex flex-row items-center justify-between border rounded-lg p-4',
                      !enableTrack && 'opacity-50'
                    )}
                  >
                    <div className="space-y-0.5 mr-2">
                      <FormLabel className="text-base">
                        {messages.settingsMapTrackIntervalLabel}
                      </FormLabel>
                      <FormDescription>
                        {messages.settingsMapTrackIntervalDescription}
                      </FormDescription>
                    </div>
                    <FormControl onChange={() => form.clearErrors()}>
                      <Input
                        className="w-20"
                        disabled={!enableTrack}
                        type="number"
                        {...field}
                        onChange={(e) => {
                          if (!e.target.value) {
                            field.onChange('');
                          } else {
                            // Convert the string value to a number
                            const numberValue = parseFloat(e.target.value);
                            field.onChange(numberValue);
                          }
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="space-y-4 border rounded-lg p-4 w-full">
                <div>
                  <FormLabel className="text-base">Position jumps</FormLabel>
                  <FormDescription>
                    Maximum distance or speed a target can jump before being
                    filtered out
                  </FormDescription>
                </div>

                <div className="flex space-x-2 w-full">
                  <FormField
                    key="track_max_position_jump_in_meters"
                    control={form.control}
                    name="track_max_position_jump_in_meters"
                    render={({ field }) => (
                      <FormItem
                        className={cn(
                          'flex flex-row items-center justify-between border rounded-lg p-4 w-full',
                          !enableTrack && 'opacity-50'
                        )}
                      >
                        <div className="space-y-0.5">
                          <FormLabel className="text-sm">
                            Maximum distance
                          </FormLabel>
                          <FormDescription>Distance in meters</FormDescription>
                        </div>
                        <FormControl onChange={() => form.clearErrors()}>
                          <Input
                            className="w-20"
                            disabled={!enableTrack}
                            type="number"
                            {...field}
                            onChange={(e) => {
                              if (!e.target.value) {
                                field.onChange('');
                              } else {
                                // Convert the string value to a number
                                const numberValue = parseFloat(e.target.value);
                                field.onChange(numberValue);
                              }
                            }}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    key="track_max_position_jump_in_knots"
                    control={form.control}
                    name="track_max_position_jump_in_knots"
                    render={({ field }) => (
                      <FormItem
                        className={cn(
                          'flex flex-row items-center justify-between border rounded-lg p-4 w-full',
                          !enableTrack && 'opacity-50'
                        )}
                      >
                        <div className="space-y-0.5">
                          <FormLabel className="text-sm">
                            Maximum speed
                          </FormLabel>
                          <FormDescription>Speed in knots</FormDescription>
                        </div>

                        <FormControl onChange={() => form.clearErrors()}>
                          <Input
                            className="w-20"
                            disabled={!enableTrack}
                            type="number"
                            {...field}
                            onChange={(e) => {
                              if (!e.target.value) {
                                field.onChange('');
                              } else {
                                // Convert the string value to a number
                                const numberValue = parseFloat(e.target.value);
                                field.onChange(numberValue);
                              }
                            }}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 pr-2">
          <Button
            id="settings-track-save-button"
            onClick={form.handleSubmit(onSubmit)}
            type="submit"
            disabled={isSubmitting}
          >
            {isSubmitting ? messages.genericSubmitting : messages.genericSubmit}
          </Button>
        </div>
      </form>
    </Form>
  );
};
